"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BackButton } from "@/components/ui/back-button";
import {
  Calendar,
  DollarSign,
  Users,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Briefcase,
  UserPlus,
  Shield,
} from "lucide-react";

type WeekendSummary = {
  period: { from: string; to: string };
  summary: {
    hires: number;
    payments: {
      total: number;
      charged: number;
      pending: number;
      revenue: number;
    };
    issues: {
      total: number;
      open: number;
      safety: number;
    };
    newSignups: number;
  };
  hires: Array<{
    id: string;
    status: string;
    hired_at: string;
    worker_id: string;
    jobs: { title: string; companies: { name: string } };
  }>;
  payments: Array<{
    id: string;
    status: string;
    amount: number;
    created_at: string;
  }>;
  issues: Array<{
    id: string;
    category: string;
    status: string;
    description: string;
    created_at: string;
  }>;
};

export default function WeekendSummaryPage() {
  const [data, setData] = useState<WeekendSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSummary() {
      try {
        const res = await fetch("/api/admin/weekend-summary");
        if (!res.ok) throw new Error("Failed to load summary");
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    }
    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-8">
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
            <p className="mt-2 text-sm text-muted-foreground">{error || "No data"}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary } = data;
  const periodFrom = new Date(data.period.from).toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const periodTo = new Date(data.period.to).toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  return (
    <div className="mx-auto max-w-screen-sm px-4 py-6">
      <BackButton />

      <div className="mt-4 flex items-center gap-2">
        <Calendar className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-bold">Weekend Summary</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {periodFrom} — {periodTo}
      </p>

      {/* Stat Cards */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <StatCard
          icon={<Briefcase className="h-5 w-5 text-primary" />}
          label="Hires"
          value={summary.hires}
        />
        <StatCard
          icon={<DollarSign className="h-5 w-5 text-green-500" />}
          label="Revenue"
          value={`$${summary.payments.revenue}`}
        />
        <StatCard
          icon={<UserPlus className="h-5 w-5 text-blue-500" />}
          label="New Signups"
          value={summary.newSignups}
        />
        <StatCard
          icon={<AlertTriangle className="h-5 w-5 text-yellow-500" />}
          label="Issues"
          value={summary.issues.total}
          alert={summary.issues.safety > 0}
        />
      </div>

      {/* Payment breakdown */}
      <Card className="mt-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Payments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" /> Charged
            </span>
            <span className="font-medium">{summary.payments.charged}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-500" /> Pending
            </span>
            <span className="font-medium">{summary.payments.pending}</span>
          </div>
        </CardContent>
      </Card>

      {/* Issues */}
      {data.issues.length > 0 && (
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Issues Reported
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.issues.map((issue) => (
              <div
                key={issue.id}
                className={`rounded-lg border p-3 ${
                  issue.category === "safety"
                    ? "border-destructive/50 bg-destructive/5"
                    : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    {issue.category === "safety" && (
                      <Shield className="h-4 w-4 text-destructive" />
                    )}
                    {issue.category.replace("_", " ")}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      issue.status === "open"
                        ? "bg-yellow-100 text-yellow-800"
                        : issue.status === "resolved"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {issue.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                  {issue.description}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(issue.created_at).toLocaleString("en-AU", {
                    timeZone: "Australia/Sydney",
                  })}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Hires list */}
      {data.hires.length > 0 && (
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Hires</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.hires.map((hire) => (
              <div key={hire.id} className="rounded-lg border p-3">
                <p className="text-sm font-medium">
                  {(hire.jobs as { title: string })?.title || "Job"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {(hire.jobs as { companies: { name: string } })?.companies?.name || "Company"} ·{" "}
                  {new Date(hire.hired_at).toLocaleString("en-AU", {
                    timeZone: "Australia/Sydney",
                  })}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No activity message */}
      {summary.hires === 0 &&
        summary.payments.total === 0 &&
        summary.issues.total === 0 && (
          <Card className="mt-4">
            <CardContent className="py-8 text-center">
              <Users className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                No weekend activity yet. First hires coming soon!
              </p>
            </CardContent>
          </Card>
        )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  alert,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  alert?: boolean;
}) {
  return (
    <Card className={alert ? "border-destructive/50" : ""}>
      <CardContent className="flex items-center gap-3 p-4">
        {icon}
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
